import { useContext, useState, useRef, useEffect } from 'react';
import { SiteContentContext } from '../context/SiteContentContext';
import { uploadEmailAttachment } from '../utils/email';

const DEFAULT_REQUEST_SUBJECT = 'Ваша заявка получена — {{event}}';
const DEFAULT_REQUEST_BODY =
`Здравствуйте, {{name}}!

Мы получили вашу заявку на участие в мероприятии «{{event}}».

Наш менеджер свяжется с вами в течение 24 часов для подтверждения и уточнения деталей.

Если у вас возникнут вопросы — напишите нам на julia@thesailingdoctor.ru или в Telegram.

С уважением,
Команда The Sailing Doctor
thesailingdoctor.ru`;

const DEFAULT_BOOKING_SUBJECT = 'Вы записаны на {{event}}';
const DEFAULT_BOOKING_BODY =
`Здравствуйте, {{name}}!

Рады сообщить, что вы успешно записаны на мероприятие «{{event}}» {{date}}.

Если у вас возникнут вопросы — пишите нам:
✉️ julia@thesailingdoctor.ru

С уважением,
Команда The Sailing Doctor
thesailingdoctor.ru`;

// Шаблон записи менеджером/админом свой для каждого типа мероприятия.
// Если для конкретного типа ничего не задано — используется общий DEFAULT_BOOKING_* как запасной.
const BOOKING_TYPES = [
  { suffix: 'Study',      label: '🎓 Обучение' },
  { suffix: 'Adventures', label: '⛵ Путешествия' },
  { suffix: 'Races',      label: '🏆 Регаты' },
];

/* ===== Пикер вложений ===== */
// attachment — строка (старый формат) или { url, name }
const attName = a => typeof a === 'string'
  ? (a.startsWith('data:') ? null : decodeURIComponent(a.split('/').pop()))
  : a.name;

const AttachmentPicker = ({ value = [], onChange }) => {
  const [urlInput, setUrlInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileRef = useRef(null);

  const addUrl = (url) => {
    if (!url) return;
    const name = decodeURIComponent(url.split('/').pop());
    onChange([...value, { url, name }]);
    setUrlInput('');
  };

  const handleFile = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    setUploadError('');
    try {
      const uploaded = await uploadEmailAttachment(file);
      onChange([...value, uploaded]);
    } catch (err) {
      setUploadError(`Не удалось загрузить файл: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {value.map((att, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f8f8f8', border: '1px solid #e0e0e0', borderRadius: 6, padding: '6px 10px' }}>
          <span>📎</span>
          <span style={{ flex: 1, fontSize: 12, color: '#555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {attName(att) || '(файл без имени)'}
          </span>
          <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))}
            style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', fontSize: 16, padding: 0 }}>✕</button>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          value={urlInput}
          onChange={e => setUrlInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addUrl(urlInput.trim()))}
          placeholder="URL файла (pdf, png...)"
          style={{ flex: 1, padding: '7px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }}
        />
        <button type="button" onClick={() => addUrl(urlInput.trim())}
          style={{ padding: '7px 14px', background: '#555', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
          + URL
        </button>
        <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
          style={{ padding: '7px 14px', background: '#db782e', color: '#fff', border: 'none', borderRadius: 6, cursor: uploading ? 'default' : 'pointer', fontSize: 13 }}>
          {uploading ? 'Загружаем...' : '📎 Файл'}
        </button>
        <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp,.gif" style={{ display: 'none' }} onChange={handleFile} />
      </div>
      {uploadError && <p style={{ fontSize: 12, color: '#c62828', margin: 0 }}>{uploadError}</p>}
      <p style={{ fontSize: 11, color: '#aaa', margin: 0 }}>Прикрепляются ко всем письмам этого типа по умолчанию.</p>
    </div>
  );
};

/* ===== Один шаблон ===== */
const TemplateBlock = ({ title, hint, subjectKey, bodyKey, attachKey, local, onChange }) => (
  <div style={{ background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.07)', marginBottom: 24 }}>
    <h3 style={{ margin: '0 0 6px', fontSize: '1rem' }}>{title}</h3>
    {hint && <p style={{ color: '#888', fontSize: '0.85rem', margin: '0 0 16px' }}>{hint}</p>}

    <div style={{ marginBottom: 14 }}>
      <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#555', display: 'block', marginBottom: 5 }}>Тема письма</label>
      <input
        value={local[subjectKey] ?? ''}
        onChange={e => onChange(subjectKey, e.target.value)}
        style={{ width: '100%', padding: '9px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: '0.95rem', boxSizing: 'border-box' }}
      />
    </div>

    <div style={{ marginBottom: 14 }}>
      <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#555', display: 'block', marginBottom: 5 }}>
        Текст письма
        <span style={{ fontWeight: 400, color: '#aaa', marginLeft: 10 }}>
          переменные: <code>{'{{firstName}}'}</code> имя · <code>{'{{name}}'}</code> полное имя · <code>{'{{event}}'}</code> мероприятие · <code>{'{{date}}'}</code> дата начала · <code>{'{{dateEnd}}'}</code> дата окончания · <code>{'{{location}}'}</code> место
        </span>
      </label>
      <textarea
        rows={10}
        value={local[bodyKey] ?? ''}
        onChange={e => onChange(bodyKey, e.target.value)}
        style={{ width: '100%', padding: '9px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: '0.9rem', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }}
      />
    </div>

    <div>
      <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#555', display: 'block', marginBottom: 8 }}>Вложения по умолчанию</label>
      <AttachmentPicker
        value={local[attachKey] || []}
        onChange={v => onChange(attachKey, v)}
      />
    </div>
  </div>
);

function buildLocalState(content) {
  const state = {
    confirmEmailEnabled:       content.confirmEmailEnabled       ?? true,
    requestConfirmSubject:     content.requestConfirmSubject     ?? DEFAULT_REQUEST_SUBJECT,
    requestConfirmBody:        content.requestConfirmBody        ?? DEFAULT_REQUEST_BODY,
    requestConfirmAttachments: content.requestConfirmAttachments ?? [],
  };
  BOOKING_TYPES.forEach(({ suffix }) => {
    state[`bookingConfirmSubject${suffix}`]     = content[`bookingConfirmSubject${suffix}`]     ?? content.bookingConfirmSubject     ?? DEFAULT_BOOKING_SUBJECT;
    state[`bookingConfirmBody${suffix}`]        = content[`bookingConfirmBody${suffix}`]        ?? content.bookingConfirmBody        ?? DEFAULT_BOOKING_BODY;
    state[`bookingConfirmAttachments${suffix}`] = content[`bookingConfirmAttachments${suffix}`] ?? content.bookingConfirmAttachments ?? [];
  });
  return state;
}

/* ===== Главный компонент ===== */
const EmailSettings = () => {
  const { content, updateContent } = useContext(SiteContentContext);
  const [local, setLocal] = useState(() => buildLocalState(content));
  const [saved, setSaved] = useState(false);

  // Синхронизируем local когда данные из БД подтянулись (content.bookingConfirmSubject появился)
  const [synced, setSynced] = useState(false);
  useEffect(() => {
    if (synced) return;
    if (!content.bookingConfirmSubjectStudy && !content.bookingConfirmSubject && !content.requestConfirmSubject) return; // ещё не загрузилось
    setLocal(buildLocalState(content));
    setSynced(true);
  }, [content, synced]);

  const onChange = (key, val) => setLocal(prev => ({ ...prev, [key]: val }));

  const handleSave = () => {
    updateContent(local);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  return (
    <div style={{ padding: 24, maxWidth: 860 }}>
      <h2 style={{ margin: '0 0 6px' }}>📧 Email-уведомления</h2>
      <p style={{ color: '#888', fontSize: '0.9rem', marginBottom: 24 }}>
        Настройте шаблоны писем, которые получают пользователи. Шаблоны доступны и менеджеру, и администратору.
      </p>

      {/* Главный тумблер */}
      <div style={{ background: '#fff', borderRadius: 10, padding: '16px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.07)', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 14 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', userSelect: 'none' }}>
          <div
            onClick={() => onChange('confirmEmailEnabled', !local.confirmEmailEnabled)}
            style={{
              width: 44, height: 24, borderRadius: 12, cursor: 'pointer',
              background: local.confirmEmailEnabled ? '#db782e' : '#ccc',
              position: 'relative', transition: 'background 0.2s', flexShrink: 0,
            }}
          >
            <div style={{
              position: 'absolute', top: 3, left: local.confirmEmailEnabled ? 22 : 2,
              width: 18, height: 18, borderRadius: '50%', background: '#fff',
              transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            }} />
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>
              {local.confirmEmailEnabled ? '✅ Письма-подтверждения включены' : '⛔ Письма-подтверждения отключены'}
            </div>
            <div style={{ fontSize: '0.8rem', color: '#888', marginTop: 2 }}>
              {local.confirmEmailEnabled
                ? 'Пользователи получают email при заявке с сайта и при записи менеджером'
                : 'Никакие письма-подтверждения не отправляются'}
            </div>
          </div>
        </label>
      </div>

      <TemplateBlock
        title="✉️ Подтверждение заявки с сайта"
        hint="Отправляется автоматически, когда пользователь нажимает «Записаться» или «Задать вопрос» на сайте."
        subjectKey="requestConfirmSubject"
        bodyKey="requestConfirmBody"
        attachKey="requestConfirmAttachments"
        local={local}
        onChange={onChange}
      />

      <h3 style={{ margin: '28px 0 4px', fontSize: '1.05rem' }}>📋 Подтверждение записи от менеджера / администратора</h3>
      <p style={{ color: '#888', fontSize: '0.85rem', margin: '0 0 16px' }}>
        Отправляется вручную, когда менеджер или администратор добавляет участника с email в панели управления. Свой шаблон для каждого типа мероприятия.
      </p>
      {BOOKING_TYPES.map(({ suffix, label }) => (
        <TemplateBlock
          key={suffix}
          title={label}
          subjectKey={`bookingConfirmSubject${suffix}`}
          bodyKey={`bookingConfirmBody${suffix}`}
          attachKey={`bookingConfirmAttachments${suffix}`}
          local={local}
          onChange={onChange}
        />
      ))}

      <div style={{ position: 'sticky', bottom: 0, background: '#f8f8f8', padding: '14px 0', borderTop: '1px solid #eee' }}>
        <button onClick={handleSave}
          style={{ background: saved ? '#4caf50' : '#db782e', color: '#fff', border: 'none', padding: '12px 32px', borderRadius: 8, fontSize: '1rem', fontWeight: 600, cursor: 'pointer', transition: 'background 0.2s' }}>
          {saved ? '✓ Сохранено' : 'Сохранить шаблоны'}
        </button>
      </div>
    </div>
  );
};

export default EmailSettings;
